import { useUiText } from "../../locale/UiLocale";
import { fmt } from "../../format";
import { Select } from "../Select/Select";
import { Pager } from "./Pager";
import type { Paging } from "./usePaging";

export interface DataTablePagerProps {
  paging: Paging;
  /** Rows on the current page. */
  pageRowCount: number;
  /** Rows after search and filters. */
  visibleCount: number;
  /** Rows before search and filters. */
  totalCount: number;
  pageSize: number;
  pageSizes: number[];
  onPageSizeChange: (size: number) => void;
}

/** Footer: which rows are shown, the page size and the pager. */
export function DataTablePager(props: DataTablePagerProps): React.JSX.Element {
  const { paging, visibleCount, pageSize, pageSizes, onPageSizeChange } = props;
  const hasPages = paging.pageCount > 1;
  return (
    <div className="nb-dt-foot">
      <DataTableRange {...props} />
      <div className="nb-row" style={{ gap: "var(--space-4)" }}>
        {visibleCount > Math.min(...pageSizes) ? (
          <PageSizeSelect pageSize={pageSize} pageSizes={pageSizes} onChange={onPageSizeChange} />
        ) : null}
        {hasPages ? <Pager currentPage={paging.currentPage} pageCount={paging.pageCount} onPageChange={paging.setPage} /> : null}
      </div>
    </div>
  );
}

/** "1–25 of 80" across pages, "3 items" on a single page, plus how many rows the filters hide. */
function DataTableRange({ paging, pageRowCount, visibleCount, totalCount }: DataTablePagerProps): React.JSX.Element {
  const t = useUiText();
  return (
    <span className="nb-dt-range" aria-live="polite">
      {paging.pageCount > 1 ? (
        <>
          <b>
            {fmt.number(paging.start + 1)}–{fmt.number(paging.start + pageRowCount)}
          </b>{" "}
          {t("of")} {fmt.number(visibleCount)}
        </>
      ) : (
        <>
          <b>{fmt.number(visibleCount)}</b> {visibleCount === 1 ? t("item") : t("items")}
        </>
      )}
      {visibleCount !== totalCount ? <span className="nb-subtle"> · {t("filtered from")} {fmt.number(totalCount)}</span> : null}
    </span>
  );
}

interface PageSizeSelectProps {
  pageSize: number;
  pageSizes: number[];
  onChange: (size: number) => void;
}

function PageSizeSelect({ pageSize, pageSizes, onChange }: PageSizeSelectProps): React.JSX.Element {
  const t = useUiText();
  return (
    <label className="nb-dt-pagesize">
      {t("Rows per page")}
      <Select
        size="sm"
        value={String(pageSize)}
        options={pageSizes.map((n) => ({ value: String(n), label: String(n) }))}
        onValueChange={(v) => onChange(Number(v))}
      />
    </label>
  );
}
